import React from 'react';
import { t } from '../../i18n/t';
import { LopButton } from '../LopButton';
import './LopHistoryDateFilter.css';
import { LopDateInput } from '../../components/LopDateInput';

// Shared filter bar for "history" screens (RatesHistory, IdxRatesHistory,
// and any future tables that grow with time). Two date inputs + a "Fetch"
// button. Controlled component — the page owns DRAFT and ACTIVE state
// separately so the user can edit dates without triggering a refetch on
// every keystroke; only "Fetch" copies draft -> active.
//
// Default range convention: two weeks back -> today (see helpers below).
// This component is purely presentational — it does not enforce defaults or
// validate ranges (page-level concern).

export interface LopHistoryDateFilterProps {
  fromDate: string;   // YYYY-MM-DD
  toDate: string;
  onFromChange: (next: string) => void;
  onToChange: (next: string) => void;
  onApply: () => unknown;
  loading?: boolean;
}

export function LopHistoryDateFilter({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  onApply,
  loading,
}: LopHistoryDateFilterProps) {
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      onApply();
    }
  }

  return (
    <div className="lop-history-date-filter">
      <label className="lop-history-date-filter__field">
        <span>{t('common.fromDate')}</span>
        <LopDateInput
          value={fromDate}
          onChange={onFromChange}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
      </label>
      <label className="lop-history-date-filter__field">
        <span>{t('common.toDate')}</span>
        <LopDateInput
          value={toDate}
          onChange={onToChange}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
      </label>
      <LopButton variant="primary" onClick={onApply} loading={loading}>
        {t('common.fetch')}
      </LopButton>
    </div>
  );
}

// Default range helpers — exported so pages can seed their state symmetrically.
// `dDate` is the SQL DATE column on history tables; we format YYYY-MM-DD here
// so the value plugs straight into <input type="date"> and into the API.
export function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function isoTwoWeeksAgo(): string {
  const d = new Date();
  d.setDate(d.getDate() - 14);
  return d.toISOString().slice(0, 10);
}
